import React, { useReducer, createContext, useContext } from "react";
import set from "lodash.set";
import { localStorageSave, StorageTypes } from "../../helpers/GlobalHelper";
import { GlobalConstants } from "../constants/GlobalConstants";

const localStorageGet = (key: string, storageType: StorageTypes = "local") => {
  let item: any = null;
  if (storageType === "local") {
    item = localStorage.getItem(key);
  }

  if (storageType === "session") {
    item = sessionStorage.getItem(key);
  }

  if (!item) return;
  if (item[0] === "{" || item[0] === "[" || item === "true" || item === "false") return JSON.parse(item);
  return item;
};

const getUpdatedTableData = (state, property, payload) => {
  return {
    ...state,
    activePageTableData: {
      ...state.activePageTableData,
      [property]: payload,
    },
  };
};

const initialActiveHistory = { currentPosition: 0, allHistory: [] };

const Reducer = (state: any, { type, payload }: any) => {
  const stateCopy = { ...state };
  let updatedFilters = payload?.tabsFilter ? [...payload.tabsFilter] : [...stateCopy?.tabsFilter || []];
  const activeTabId = payload?.newActiveTabId || stateCopy?.activeTab?.id || "";
  const allHistory = payload?.newActiveTabId ? updatedFilters.find(filter => filter.id === payload?.newActiveTabId)?.history?.allHistory || [] : stateCopy?.activeHistory?.allHistory || [];
  const initialLocation = allHistory?.length === 0 && payload?.history?.action === "POP" ? [{ ...payload?.history?.location, isCurrent: true }] : [];
  const updatedAllHistory = payload?.allHistory ? payload?.allHistory : allHistory?.length > 0 ? allHistory?.map(history => ({ ...history, isCurrent: false })) : initialLocation;
  let activeHistoryCopy = {
    ...(updatedFilters?.find(tab => tab.id === activeTabId)?.history || stateCopy?.activeHistory || {}),
    allHistory: updatedAllHistory,
    activeTabId: activeTabId
  };

  switch (type) {
    case GlobalConstants.LMSStoreReducerCases.SET_FILTER_TABS:
      if (payload?.length === 0) {
        localStorageSave("activeTab", {});
      }

      return {
        ...state,
        tabsFilter: payload,
      };
    case GlobalConstants.LMSStoreReducerCases.ADD_FILTER_TAB:
      updatedFilters = [
        ...state?.tabsFilter?.map(tab => {
          if (tab.id === state.activeTab.id) {
            return { ...tab, history: state.activeHistory, isActive: false };
          }

          return { ...tab, isActive: false };
        }),
        { ...payload, isActive: true },
      ];

      localStorageSave("activeTab", payload);
      localStorageSave("tabsFilter", updatedFilters);
      localStorageSave("activeHistory", { ...initialActiveHistory, activeTabId: payload.id || "" });
      return {
        ...state,
        tabsFilter: updatedFilters,
        activeTab: payload,
        activeHistory: { ...initialActiveHistory, activeTabId: payload.id || "" },
      };

    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_TAB:
      updatedFilters = updatedFilters?.map((tab) => {
        if (tab.id === state?.activeTab?.id) {
          return { ...tab, history: state.activeHistory, isActive: false };
        }
        else if (tab.id === payload.id) {
          activeHistoryCopy = { ...tab?.history || {}, activeTabId: payload.id || "" };
          return { ...tab, isActive: true };
        }
        else {
          return { ...tab, isActive: false };
        }
      });

      localStorageSave("activeTab", payload);
      localStorageSave("tabsFilter", updatedFilters);
      localStorageSave("activeHistory", { ...(updatedFilters?.find(tab => tab.id === payload.id)?.history || activeHistoryCopy || {}) });
      return {
        ...state,
        tabsFilter: updatedFilters,
        activeTab: payload,
        activeHistory: { ...(updatedFilters?.find(tab => tab.id === payload.id)?.history || activeHistoryCopy || {}) }
      };

    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PATH:
      updatedFilters = updatedFilters?.map((tab) => {
        if (tab.id === activeTabId) {
          return { ...tab, isActive: true, path: payload.path, history: state.activeHistory };
        }

        return { ...tab, isActive: false };
      });

      localStorageSave("activeTab", { ...payload, tabHasUpdatedForm: false });
      localStorageSave("tabsFilter", updatedFilters);
      localStorageSave("activeHistory", activeHistoryCopy);
      return {
        ...state,
        tabsFilter: updatedFilters,
        activeTab: { ...payload, tabHasUpdatedForm: false },
        activeHistory: activeHistoryCopy
      };
    case GlobalConstants.LMSStoreReducerCases.REMOVE_TAB:
      updatedFilters = state.tabsFilter?.filter(tab => tab.id !== payload);
      localStorageSave("activeTab", state.activeTab?.id === payload ? {} : state.activeTab);
      localStorageSave("tabsFilter", updatedFilters);
      localStorageSave("activeHistory", state.activeTab?.id === payload ? initialActiveHistory : activeHistoryCopy);
      return {
        ...state,
        tabsFilter: updatedFilters,
        activeTab: state.activeTab?.id === payload ? {} : state.activeTab,
        activeHistory: state.activeTab?.id === payload ? initialActiveHistory : activeHistoryCopy
      };
    case GlobalConstants.LMSStoreReducerCases.REMOVE_ALL_TABS:
      updatedFilters = state.tabsFilter?.filter(tab => tab.id !== payload);
      localStorageSave("activeTab", {});
      localStorageSave("tabsFilter", []);
      localStorageSave("activeHistory", initialActiveHistory);
      return {
        ...state,
        tabsFilter: [],
        activeTab: {},
        activeHistory: initialActiveHistory
      };
    case GlobalConstants.LMSStoreReducerCases.CLEAR_ACTIVE_TAB:
      updatedFilters = updatedFilters?.map((tab) => {
        if (tab.id === activeTabId) {
          return { ...tab, isActive: false, history: state.activeHistory };
        }

        return { ...tab, isActive: false };
      });

      localStorageSave("activeTab", {});
      localStorageSave("tabsFilter", updatedFilters);
      localStorageSave("activeHistory", initialActiveHistory);
      return {
        ...state,
        tabsFilter: updatedFilters,
        activeTab: {},
        activeHistory: initialActiveHistory
      };
    case GlobalConstants.LMSStoreReducerCases.SET_CONFIRM_OPTIONS:
      return {
        ...state,
        confirmOptions: payload,
      };
    case GlobalConstants.LMSStoreReducerCases.SET_API_CACHE:
      stateCopy.apiCache[payload.apiSuffix] = payload?.data;
      return stateCopy;
    case GlobalConstants.LMSStoreReducerCases.SET_SELECTED_DATA:
      if (payload.selectorString) {
        return set(stateCopy, payload.selectorString, payload.data);
      }

      return state;
    case GlobalConstants.LMSStoreReducerCases.SET_TAB_HAS_UPDATED_FORM:
      return {
        ...state,
        activeTab: { ...state.activeTab, tabHasUpdatedForm: true },
      };
    case GlobalConstants.LMSStoreReducerCases.SET_TABLE_CACHE:
      return {
        ...state,
        tableCache: {
          ...state.tableCache || {},
          [payload?.activeTabId]: {
            ...state.tableCache?.[payload?.activeTabId] || {},
            [payload.tableId]: payload.data,
          }
        }
      };
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE_ELEMENTS:
      return {
        ...state,
        activePageTableData: {
          ...state.activePageTableData,
          ...payload,
        },
      };
    case GlobalConstants.LMSStoreReducerCases.RESET_ACTIVE_PAGE_ELEMENTS:
      return {
        ...state,
        activePageTableData: {},
      };
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE_ACTION_BUTTONS:
      return getUpdatedTableData(state, "actionButtons", payload);
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE:
      return getUpdatedTableData(state, "activePagePath", payload);
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE_DATA:
      return getUpdatedTableData(state, "data", payload);
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE_SORTED_DATA:
      return getUpdatedTableData(state, "sortedData", payload);
    case GlobalConstants.LMSStoreReducerCases.SET_ACTIVE_PAGE_CHECKED_ITEMS:
      return getUpdatedTableData(state, "checkedItems", payload);
    default:
      return state;
  }
};

const activeTab = localStorageGet("activeTab") || {};
const initialState: any = {
  tabsFilter: localStorageGet("tabsFilter") || [activeTab],
  activeTab: activeTab,
  activeHistory: localStorageGet("activeHistory") || initialActiveHistory,
  appHistory: [],
  activePageTableData: {},
  apiCache: {},
};

const LMSGlobalStore = ({ children }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);
  return (
    <Context.Provider value={[state, dispatch]}>{children}</Context.Provider>
  );
};

export const Context = createContext(initialState);
export const useLMSGlobalReducer = () => useContext(Context);
export default LMSGlobalStore;